import { inject, Injectable, signal } from '@angular/core';
import { CurrentUserService } from '../../../common/current-user/current-user.service';
import { ChatGptService } from '../../../services/chat-gpt/chat-gpt.service';
import { GptCompletionMessage } from '../../../common/general-types/open-ai.dto';
import { CreateScreeningService } from '../../screening-brief-create/services/create-screening/create-screening.service';

/** Delimiter the model emits between its free-text reasoning and the final JSON object. */
const JSON_DELIMITER = '<<<BRIEF_JSON>>>';

/** The shape the model must produce after the delimiter for a screening brief. */
export interface ScreeningDraftResult {
  briefTitle?: string;
  /** HTML overview of what this screening is for. */
  briefText?: string;
  /** HTML list of signals content MUST demonstrate to pass screening. */
  contentRequirements?: string;
  /** HTML list of red-flags / disqualifiers. */
  contentDonts?: string;
}

/**
 * Screening-brief equivalent of BriefAiService: builds a content-screening prompt on the
 * frontend and streams reasoning + JSON through the generic /chat-gpt/stream proxy, then
 * applies the parsed draft into CreateScreeningService. No backend-specific endpoint needed.
 */
@Injectable({ providedIn: 'root' })
export class ScreeningBriefAiService {
  private readonly currentUserService = inject(CurrentUserService);
  private readonly createScreeningService = inject(CreateScreeningService);
  private readonly chatGptService = inject(ChatGptService);

  readonly isDrafting = signal(false);
  readonly isCompilingBrief = signal(false);
  readonly streamingThoughts = signal('');
  readonly draftError = signal<string | undefined>(undefined);

  /**
   * Streams a full screening-brief draft and populates the screening signals
   * (title, brief overview, content requirements, don'ts).
   * @returns true on success.
   */
  async draftScreeningBrief(anglePrompt: string): Promise<boolean> {
    if (this.isDrafting()) {
      return false;
    }

    this.isDrafting.set(true);
    this.isCompilingBrief.set(false);
    this.streamingThoughts.set('');
    this.draftError.set(undefined);

    let full = '';
    try {
      for await (const delta of this.chatGptService.streamCompletionMessage(this.buildMessages(anglePrompt))) {
        full += delta;
        this.streamingThoughts.set(this.extractThoughts(full));
        if (full.includes(JSON_DELIMITER)) {
          this.isCompilingBrief.set(true);
        }
      }

      const parsed = this.parseDraftFromResponse(full);
      if (!parsed) {
        this.draftError.set('The AI response could not be read. Please try again.');
        return false;
      }

      return this.applyDraftResult(parsed);
    } catch (err) {
      console.error('ScreeningBriefAiService.draftScreeningBrief() failed', err);
      this.draftError.set('Something went wrong while generating your screening brief. Please try again.');
      return false;
    } finally {
      this.isDrafting.set(false);
      this.isCompilingBrief.set(false);
    }
  }

  /** Applies a parsed GPT draft into CreateScreeningService signals. */
  applyDraftResult(parsed: ScreeningDraftResult): boolean {
    console.log('applyDraftResult', parsed);
    const service = this.createScreeningService;
    const title = parsed.briefTitle?.trim();
    if (title) {
      service.basicInfoModel.set({ briefTitle: title });
    }
    service.briefTextHtml.set(parsed.briefText ?? '');
    service.requirementsHtml.set(parsed.contentRequirements ?? '');
    service.restrictionsHtml.set(parsed.contentDonts ?? '');
    // Always default the screen-until date to 3 months ago (must be in the past).
    service.screenContentUntil.set(this.threeMonthsAgo());
    // Ensure the review's requirements section renders the editor (not the upload/AI branches).
    service.creativeMethod.set('type');
    return true;
  }

  /** A date exactly 3 months before now — the default lookback for AI-drafted screening briefs. */
  private threeMonthsAgo(): Date {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date;
  }

  private buildMessages(anglePrompt: string): GptCompletionMessage[] {
    const brand = this.currentUserService.focussedBrand;
    const product = this.createScreeningService.selectedProduct();
    const briefTitle = this.createScreeningService.basicInfoModel()?.briefTitle;

    const brandName = brand?.companyName ?? '';
    const brandAbout = brand?.about ?? '';
    const productName = product?.productName ?? '';
    const productDescription = product?.productDescription ?? '';

    const systemMessage: GptCompletionMessage = {
      role: 'system',
      content: `You are a brand-safety and content-screening expert helping a brand define what to look for when screening a creator's existing social content.
        A "screening brief" is used to evaluate a creator's PAST content (months or years of previously published posts) against a set of requirements — it does NOT commission new content, so never reference filming, shooting, captions, hashtags, deadlines or deliverables.
        First, narrate your reasoning out loud in plain, friendly prose so the user can follow your thinking — do NOT use JSON during this part.
        When you have finished reasoning, output the exact delimiter line ${JSON_DELIMITER} on its own line, then output ONLY a single valid, RFC8259 compliant JSON object and nothing after it.
        Do not wrap the JSON in markdown code fences. Never mention the term "JSON" at any point.`,
    };

    const userMessage: GptCompletionMessage = {
      role: 'user',
      content: `This is my brand name: ${brandName}
      This is my brand description: ${brandAbout}
      ${briefTitle ? 'This is the current screening brief title: ' + briefTitle : ''}
      This is the product this screening relates to: ${productName}
      This is the product description: ${productDescription}

      We are screening creators' existing published content to find creators whose past posts already align with this brand and product.

      Here is the format of the JSON object you must produce after the ${JSON_DELIMITER} delimiter:
      {
        "briefTitle": string,
        "briefText": string,
        "contentRequirements": string,
        "contentDonts": string
      }

      briefTitle is a punchy Title Case name for this screening brief tied to this brand and product (max ~80 characters).
      briefText is an HTML string giving an overview of this screening: use headings "Overview" (why we are screening and what a strong match looks like) and "What we're screening for" (the themes, tone and topics a creator's past content should show).
      contentRequirements is an HTML list (<ul><li>) of concrete, checkable signals a screener or AI can verify in a creator's PAST posts — specific to this brand and product (e.g. genuine mentions of the product category, on-brand aesthetic, an engaged relevant audience). Make each item objectively verifiable, not vague.
      contentDonts is an HTML list (<ul><li>) of disqualifiers / red flags that should fail screening (e.g. promotion of competing brands, off-brand or explicit content, misleading claims, content unrelated to the category, inactive or stale accounts). Rewrite these specifically for this brand/product.
      Use Tailwind classes in the HTML. Write SPECIFIC criteria tied to this exact brand and product — not generic ones.
      Do NOT mention JSON in your reasoning prose.

      What the brand wants to screen for: ${anglePrompt}`,
    };

    return [systemMessage, userMessage];
  }

  private parseDraftFromResponse(full: string): ScreeningDraftResult | null {
    const jsonText = this.extractJson(full);
    if (!jsonText) {
      return null;
    }
    try {
      return JSON.parse(jsonText) as ScreeningDraftResult;
    } catch (err) {
      console.error('ScreeningBriefAiService.parseDraftFromResponse() JSON parse failed', err);
      return null;
    }
  }

  private extractJson(full: string): string | undefined {
    let candidate = full;
    const delimiterIndex = full.indexOf(JSON_DELIMITER);
    if (delimiterIndex >= 0) {
      candidate = full.slice(delimiterIndex + JSON_DELIMITER.length);
    }
    candidate = candidate.replace(/```json/gi, '').replace(/```/g, '').trim();
    const start = candidate.indexOf('{');
    const end = candidate.lastIndexOf('}');
    if (start === -1 || end === -1 || end <= start) {
      return undefined;
    }
    return candidate.slice(start, end + 1);
  }

  private extractThoughts(full: string): string {
    const delimiterIndex = full.indexOf(JSON_DELIMITER);
    return delimiterIndex >= 0 ? full.slice(0, delimiterIndex) : full;
  }
}
